class LetoileDesktopCatalogMenu {
  constructor(root) {
    if (!root || root.dataset.desktopCatalogBound === 'true') return;

    this.root = root;
    this.header = root.closest('[data-site-header]');
    this.trigger = this.header?.querySelector(`[data-desktop-catalog-trigger][aria-controls="${CSS.escape(root.id)}"]`);
    this.surface = root.querySelector('[data-desktop-catalog-surface]');
    this.rootTriggers = [...root.querySelectorAll('[data-desktop-catalog-root]')];
    this.panels = [...root.querySelectorAll('[data-desktop-catalog-panel]')];

    if (!this.header || !this.trigger || !this.surface || !this.rootTriggers.length || !this.panels.length) return;

    root.dataset.desktopCatalogBound = 'true';
    this.bind();
  }

  bind() {
    this.trigger.addEventListener('click', () => {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    });

    this.root.addEventListener('click', (event) => {
      const rootTrigger = event.target.closest('[data-desktop-catalog-root]');
      if (rootTrigger) {
        this.select(rootTrigger.dataset.desktopCatalogRoot, rootTrigger);
        return;
      }

      if (event.target.closest('[data-desktop-catalog-close]')) this.close();
    });

    document.addEventListener('keydown', (event) => {
      if (this.isOpen()) this.onKeydown(event);
    });
    window.addEventListener('resize', () => {
      if (this.isOpen()) this.syncTop();
    });
  }

  isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  syncTop() {
    const bottom = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
    this.root.style.setProperty('--le-catalog-menu-top', `${bottom}px`);
  }

  open() {
    document.querySelectorAll('[data-desktop-catalog-trigger][aria-expanded="true"]').forEach((trigger) => {
      if (trigger !== this.trigger) trigger.click();
    });

    this.previousFocus = document.activeElement;
    this.syncTop();
    this.root.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('le-desktop-catalog-menu-open');
    this.emit('open');

    window.requestAnimationFrame(() => {
      const activeRoot = this.rootTriggers.find((trigger) => trigger.classList.contains('is-active')) || this.rootTriggers[0];
      activeRoot?.focus({ preventScroll: true });
    });
  }

  close({ returnFocus = true } = {}) {
    if (!this.isOpen()) return;

    this.trigger.setAttribute('aria-expanded', 'false');
    this.root.hidden = true;
    document.documentElement.classList.remove('le-desktop-catalog-menu-open');
    this.emit('close');
    if (returnFocus) this.trigger.focus({ preventScroll: true });
  }

  select(key, trigger) {
    const panel = this.panels.find((candidate) => candidate.dataset.desktopCatalogPanel === key);
    if (!panel) return;

    this.rootTriggers.forEach((candidate) => {
      const active = candidate === trigger;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
    this.panels.forEach((candidate) => {
      candidate.hidden = candidate !== panel;
    });
    this.root.querySelector('.le-desktop-catalog-menu__content')?.scrollTo({ top: 0, behavior: 'auto' });
    this.emit('navigate', { key });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = [
      this.trigger,
      ...this.root.querySelectorAll('button:not([tabindex="-1"]), a[href], [tabindex]:not([tabindex="-1"])'),
    ].filter((element) => !element.closest('[hidden]'));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  emit(action, detail = {}) {
    document.dispatchEvent(new CustomEvent('letoile:catalog-menu', {
      detail: {
        action,
        source: this.root.dataset.catalogSource || '',
        ...detail,
      },
    }));
  }
}

function bindDesktopCatalogMenus(root = document) {
  root.querySelectorAll('[data-desktop-catalog]').forEach((menu) => new LetoileDesktopCatalogMenu(menu));
}

bindDesktopCatalogMenus();
document.addEventListener('shopify:section:load', (event) => bindDesktopCatalogMenus(event.target));
